import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Universal text input. Apple-flavoured: 11px focus ring with brand-color
 * tint, soft elevated surface, predictable disabled state.
 *
 * Touch quality: `inputMode` is auto-derived from `type` (email/numeric/etc)
 * so iOS pops the right keyboard. `enterkeyhint` propagates to suggest
 * Done/Next/Search on the soft keyboard.
 */

type StringInputType =
  | 'text'
  | 'email'
  | 'password'
  | 'tel'
  | 'url'
  | 'number'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'file'
  | 'month'
  | 'week'
  | 'color'
  | 'hidden';

export interface InputProps extends React.ComponentProps<'input'> {
  icon?: React.ReactNode;
  /** Right-side affordance (clear button is built-in via `onClear`). */
  trailing?: React.ReactNode;
  /** Show a clear (×) button at right when value is non-empty. */
  onClear?: () => void;
  /** Visually invalid (aria-invalid mirror). */
  invalid?: boolean;
}

function inferInputMode(type: StringInputType | undefined): React.InputHTMLAttributes<HTMLInputElement>['inputMode'] | undefined {
  switch (type) {
    case 'email':
      return 'email';
    case 'number':
      return 'numeric';
    case 'tel':
      return 'tel';
    case 'url':
      return 'url';
    case 'search':
      return 'search';
    default:
      return undefined;
  }
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      icon,
      trailing,
      onClear,
      invalid,
      inputMode,
      autoComplete,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const value = (props.value ?? props.defaultValue) as string | number | undefined;
    const showClear = !!onClear && !!value;

    const inputEl = (
      <input
        type={type}
        ref={ref}
        inputMode={inputMode ?? inferInputMode(type as StringInputType)}
        autoComplete={autoComplete ?? (type === 'password' ? 'current-password' : undefined)}
        aria-invalid={invalid || undefined}
        className={cn(
          'flex h-9 w-full rounded-[8px] border bg-[var(--color-surface-elevated)] px-3 py-1 text-sm shadow-sm transition-[border-color,box-shadow,background-color]',
          'border-[var(--color-border)]',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-[var(--color-text)]',
          'placeholder:text-[var(--color-text-muted)]',
          'focus-visible:outline-none focus-visible:border-[var(--color-primary)]/60 focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/30',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'autofill:[-webkit-text-fill-color:var(--color-text)] autofill:[box-shadow:inset_0_0_0_1000px_var(--color-surface-elevated)]',
          invalid &&
            'border-[var(--color-destructive)]/60 focus-visible:border-[var(--color-destructive)] focus-visible:ring-[var(--color-destructive)]/30',
          icon && 'pl-9',
          (showClear || trailing) && 'pr-9',
          className,
        )}
        style={{ color: 'var(--color-text)' }}
        {...props}
      />
    );

    if (!icon && !showClear && !trailing) return inputEl;

    return (
      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] [&_svg]:h-4 [&_svg]:w-4">
            {icon}
          </div>
        )}
        {inputEl}
        {showClear && (
          <button
            type="button"
            onClick={onClear}
            tabIndex={-1}
            aria-label={t('layoutUi:input.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {!showClear && trailing && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] [&_svg]:h-4 [&_svg]:w-4">
            {trailing}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
